import { type ReactNode } from 'react';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import logoIcon from '@/assets/logo-icon.svg';
import {
  useAuth,
  useAccountStatus,
  useIsGameRulesGated,
} from '@/hooks/use-auth';
import { useSidebarStore } from '@/stores/sidebar';
import { SidebarNav } from './sidebar-nav';
import { SidebarFooter } from './sidebar-footer';
import { cn } from '@/utils/cn';

function SidebarHeader({ collapsed }: { collapsed: boolean }): ReactNode {
  return (
    <div className="flex h-14 items-center gap-sm px-md">
      <img src={logoIcon} alt="Debook" className="h-7 w-7 shrink-0" />
      <div
        className={cn(
          'flex shrink-0 items-baseline gap-1 whitespace-nowrap transition-opacity duration-150',
          collapsed ? 'opacity-0' : 'opacity-100'
        )}
      >
        <span className="font-display text-sm font-bold tracking-tight text-text-primary">
          DEBOOK
        </span>
        <span className="text-[10px] font-medium text-brand-red">
          The 3333 Games
        </span>
      </div>
    </div>
  );
}

export function SidebarToggle(): ReactNode {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  const Icon = collapsed ? ChevronsRight : ChevronsLeft;
  return (
    <button
      onClick={toggle}
      className="mt-4 flex h-6 w-6 items-center justify-center rounded-full border border-border-subtle bg-surface text-text-tertiary shadow-sm transition-colors hover:bg-surface-elevated hover:text-text-primary"
      aria-label="Toggle sidebar"
    >
      <Icon size={12} />
    </button>
  );
}

const ASIDE_BASE =
  'flex h-full flex-col overflow-hidden border-r border-border-subtle bg-surface transition-[width] duration-200';

type SidebarState = {
  isAdmin: boolean;
  isCollector: boolean;
  collapsed: boolean;
  isVerifying: boolean;
  isGameRulesGated: boolean;
  lockAll: boolean;
};

function useSidebarState(): SidebarState {
  const { isAdmin, isCollector, user } = useAuth();
  const collapsed = useSidebarStore((s) => s.collapsed);
  const isVerifying = useAccountStatus() === 'verifying';
  const isGameRulesGated = useIsGameRulesGated();
  const missingProof = !user?.verification_photo || !user?.ig_username;
  const lockAll = isVerifying && missingProof;
  return {
    isAdmin,
    isCollector,
    collapsed,
    isVerifying,
    isGameRulesGated,
    lockAll,
  };
}

export function Sidebar(): ReactNode {
  const s = useSidebarState();
  const width = s.collapsed ? 'w-14' : 'w-[220px] max-w-[80vw]';
  return (
    <aside className={cn(ASIDE_BASE, width)}>
      <SidebarHeader collapsed={s.collapsed} />
      <SidebarNav {...s} />
      <SidebarFooter
        collapsed={s.collapsed}
        lockAll={s.lockAll}
        isGameRulesGated={s.isGameRulesGated}
      />
    </aside>
  );
}
